/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsUnlink } from "../funcs/skillsUnlink.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UnlinkSkillRequest,
  UnlinkSkillSecurity,
} from "../models/operations/unlinkskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UnlinkSkillMutationVariables = {
  request: UnlinkSkillRequest;
  security?: UnlinkSkillSecurity | undefined;
  options?: RequestOptions;
};

export type UnlinkSkillMutationData = void;

export type UnlinkSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * unlink skills
 *
 * @remarks
 * Remove a skill link so its MCP surface stops serving the skill through it. Removing a link that is already gone is a no-op. Requires the skills-over-mcp rollout for the organization.
 */
export function useUnlinkSkillMutation(
  options?: MutationHookOptions<
    UnlinkSkillMutationData,
    UnlinkSkillMutationError,
    UnlinkSkillMutationVariables
  >,
): UseMutationResult<
  UnlinkSkillMutationData,
  UnlinkSkillMutationError,
  UnlinkSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUnlinkSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyUnlinkSkill(): MutationKey {
  return ["@gram/client", "skills", "unlink"];
}

export function buildUnlinkSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UnlinkSkillMutationVariables,
  ) => Promise<UnlinkSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyUnlinkSkill(),
    mutationFn: function unlinkSkillMutationFn({
      request,
      security,
      options,
    }): Promise<UnlinkSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsUnlink(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
